import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { getUniqId } from '@mm/utils';
import {
  selectActiveCompanyId,
  activeChatbotIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { Box } from '@mm/components';
import { selectFacebookPageErrors } from '@mm/state/selectors/errorsSelector';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { useFacebookErrorCRUDActions } from '@mm/state/actions/FacebookErrorCRUDActions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import {
  ArrowStatefulIcon,
  ExclamationIcon,
  ExclamationTriangleSolidIcon,
} from '@mm/icons';
import './FacebookPageErrors.pcss';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import {
  ERROR_TYPES,
  ErrorNumber,
  MESSAGES_EXCEPTION_ACTION,
  TEMPORARY_ISSUE_HEADER,
  IGNORED_ERROR_TYPES,
} from './constants';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

const RenderHTML = props => (
  <span dangerouslySetInnerHTML={{ __html: props.HTML }} />
);

export const FacebookPageErrors = () => {
  const companyId = useSelector(selectActiveCompanyId);
  const errors = useSelector(selectFacebookPageErrors)?.filter(
    error =>
      !IGNORED_ERROR_TYPES.find(
        item =>
          item.code === error?.code &&
          item.subCode === error?.subCode &&
          item.number === error?.details?.number,
      ),
  );
  const page = useSelector(selectActiveFacebookPage);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const hasAutomationsFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).automations;
  const facebookErrorCRUDActions = useFacebookErrorCRUDActions();
  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const [activeIndex, setActiveIndex] = useState(0);

  const setIndex = (value: number) => {
    setActiveIndex(value);
  };

  const getNextIndex = () =>
    activeIndex === errors?.length - 1 ? 0 : activeIndex + 1;

  const getPrevIndex = () =>
    activeIndex === 0 ? errors?.length - 1 : activeIndex - 1;

  const next = () => {
    setIndex(getNextIndex());
  };

  const previous = () => {
    setIndex(getPrevIndex());
  };

  useEffect(() => {
    setIndex(0);
  }, [errors?.length]);

  const deleteFacebookError = (
    pageId: number,
    errorId: number,
    ignore: boolean,
  ) => {
    facebookErrorCRUDActions.delete(pageId, errorId, ignore);
  };

  const getConnectedPage = (
    ...args: Parameters<typeof connectedPageCRUDActions.retrievePage>
  ) => {
    connectedPageCRUDActions.retrievePage(...args);
  };

  const getConnectedPages = (companyId: number, pagination) => {
    connectedPageCRUDActions.retrieveAllByCompany(companyId, pagination);
  };

  if (!page || !errors || (errors && !errors.length)) {
    return null;
  }

  return (
    <div className="facebook-page-errors__wrapper">
      <Box
        className={classNames('facebook-page-errors__box', {
          oldStyle: !hasAutomationsFlag,
          warning:
            errors[activeIndex]?.severity === 'warning' ||
            errors[activeIndex]?.severity === 'info',
        })}
      >
        {errors.length > 1 && (
          <div className="facebook-page-errors__pagination">
            <PrimaryButton
              onClick={previous}
              className="facebook-page-errors__pagination-button"
              disabled={activeIndex === 0}
            >
              <ArrowStatefulIcon
                direction="left"
                size={15}
                color={activeIndex !== 0 ? '#262626' : '#BFBFBF'}
              />
            </PrimaryButton>
            <div className="facebook-page-errors__pagination-qty">
              {' '}
              {activeIndex + 1}/ {errors.length}
            </div>
            <PrimaryButton
              onClick={next}
              className="facebook-page-errors__pagination-button"
              disabled={getNextIndex() < activeIndex}
            >
              <ArrowStatefulIcon
                direction="right"
                size={15}
                color={getNextIndex() > activeIndex ? '#262626' : '#BFBFBF'}
              />
            </PrimaryButton>
          </div>
        )}
        {errors?.map((facebookError, index) => {
          const fbCode = facebookError.code;
          const fbSubCode = facebookError.subCode;

          const fbError =
            facebookError.details.number === ErrorNumber.IGMessagingException &&
            fbCode
              ? ERROR_TYPES[facebookError.details.number][fbCode]
              : facebookError.details.number ===
                  ErrorNumber.FBMessagingException &&
                fbCode &&
                fbSubCode &&
                facebookError.errorType !== 'messages_blocked_exception'
              ? ERROR_TYPES[facebookError.details.number][fbCode][fbSubCode]
              : ERROR_TYPES[facebookError.details.number];

          const header = fbError ? fbError.header : TEMPORARY_ISSUE_HEADER;
          const ActionComponent = fbError
            ? fbError.action
            : MESSAGES_EXCEPTION_ACTION;
          return (
            <div key={getUniqId('error')}>
              {activeIndex === index && (
                <div className="facebook-page-errors__description">
                  <div className="facebook-page-errors__icon">
                    {facebookError?.severity === 'fatal' ||
                    facebookError?.severity === 'error' ? (
                      <ExclamationTriangleSolidIcon
                        className="facebook-page-errors__tab-icon"
                        color="#CF1322"
                      />
                    ) : (
                      <ExclamationIcon
                        className="facebook-page-errors__tab-icon"
                        color="#FA8C16"
                      />
                    )}
                  </div>
                  <div
                    key={facebookError.id}
                    className="facebook-page-errors__body-wrapper"
                  >
                    {header}
                    {fbError ? (
                      fbError.body
                    ) : (
                      <RenderHTML
                        HTML={
                          facebookError.details.message ||
                          facebookError.originalMessage
                        }
                        key={facebookError.id}
                      />
                    )}
                    <div className="facebook-page-errors__actions-wrapper">
                      <ActionComponent
                        chatbotId={chatbotId}
                        companyId={companyId}
                        pageId={page.id}
                        deleteFacebookError={deleteFacebookError}
                        errorId={facebookError.id}
                        getConnectedPage={getConnectedPage}
                        getConnectedPages={getConnectedPages}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </Box>
    </div>
  );
};
